import * as React from "react";
import { cn } from "../../lib/utils";

interface AccordionContextValue {
  openItems: Set<string>;
  toggle: (value: string) => void;
}

const AccordionContext = React.createContext<AccordionContextValue | null>(
  null,
);

export interface AccordionProps {
  children: React.ReactNode;
  defaultValue?: string[];
  value?: string[];
  onValueChange?: (value: string[]) => void;
  className?: string;
}

export const Accordion = ({
  children,
  defaultValue = [],
  value,
  onValueChange,
  className,
}: AccordionProps) => {
  const [internal, setInternal] = React.useState<Set<string>>(
    () => new Set(defaultValue),
  );
  const openItems = value ? new Set(value) : internal;

  const toggle = (v: string) => {
    const next = new Set(openItems);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    if (value === undefined) setInternal(next);
    onValueChange?.(Array.from(next));
  };

  return (
    <AccordionContext.Provider value={{ openItems, toggle }}>
      <div className={cn("flex flex-col gap-2", className)}>{children}</div>
    </AccordionContext.Provider>
  );
};

interface AccordionItemContextValue {
  value: string;
  open: boolean;
  toggle: () => void;
}

const AccordionItemContext =
  React.createContext<AccordionItemContextValue | null>(null);

export interface AccordionItemProps extends React.HTMLAttributes<HTMLDivElement> {
  value: string;
}

export const AccordionItem = ({
  value,
  className,
  children,
  ...props
}: AccordionItemProps) => {
  const ctx = React.useContext(AccordionContext);
  if (!ctx) throw new Error("AccordionItem must be inside Accordion");
  const open = ctx.openItems.has(value);
  return (
    <AccordionItemContext.Provider
      value={{ value, open, toggle: () => ctx.toggle(value) }}
    >
      <div
        data-state={open ? "open" : "closed"}
        className={cn(className)}
        {...props}
      >
        {children}
      </div>
    </AccordionItemContext.Provider>
  );
};

export const AccordionTrigger = ({
  className,
  children,
  onClick,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) => {
  const ctx = React.useContext(AccordionItemContext);
  if (!ctx) throw new Error("AccordionTrigger must be inside AccordionItem");
  return (
    <button
      type="button"
      aria-expanded={ctx.open}
      onClick={(e) => {
        ctx.toggle();
        onClick?.(e);
      }}
      className={cn("flex w-full items-center text-left", className)}
      {...props}
    >
      {children}
    </button>
  );
};

export const AccordionContent = ({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
  const ctx = React.useContext(AccordionItemContext);
  if (!ctx) throw new Error("AccordionContent must be inside AccordionItem");
  if (!ctx.open) return null;
  return (
    <div className={cn(className)} {...props}>
      {children}
    </div>
  );
};
